import { Component, OnInit, inject, signal, computed, effect, untracked, input } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormArray } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { InventoryService } from '@servicios/inventory/inventory.service';
import { MessageService } from 'primeng/api';
import { BranchService } from '@servicios/branch.service';
import { AuthenticationService } from '@servicios/authentication.service';
import { firstValueFrom } from 'rxjs';

import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { TableModule } from 'primeng/table';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';
import { DatePickerModule } from 'primeng/datepicker';
import { TagModule } from 'primeng/tag';

import { SkeletonModule } from 'primeng/skeleton';

@Component({
  selector: 'app-stock-movement-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    TableModule,
    DropdownModule,
    InputNumberModule,
    AutoCompleteModule,
    RippleModule,
    TooltipModule,
    DatePickerModule,
    TagModule,
    SkeletonModule
  ],
  templateUrl: './stock-movement-form.component.html',
  styleUrls: ['./stock-movement-form.component.scss']
})
export class StockMovementFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private inventoryService = inject(InventoryService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private location = inject(Location);
  private branchService = inject(BranchService);
  private authService = inject(AuthenticationService);

  branchId = input<string | null>(null);
  localBranches = signal<any[]>([]);
  userBranches = signal<any[]>([]);

  mode = signal<'create' | 'edit'>('create');
  loading = signal(true);
  isSaving = signal(false);
  
  // Condense state
  optionsCondensed = signal(false);

  form: FormGroup = this.fb.group({
    id: [''],
    v_: [0],
    branchId: [null],
    destinationBranchId: [null],
    type: ['', Validators.required],
    description: [''],
    items: this.fb.array([], Validators.required)
  });

  selectedType = signal<string>('');
  availableItems = signal<any[]>([]);
  invalidItems = signal<Set<string>>(new Set());
  errorMessage = signal<string | null>(null);

  // Dynamic filtered available items based on movement type
  filteredAvailableItems = computed(() => {
    const items = this.availableItems();
    const type = this.selectedType();

    if (type === 'UNBLOCK') {
      return items
        .filter(i => i.blocked_stock > 0)
        .map(i => ({
          ...i,
          label: `[${i.internal_code || 'S/C'}] ${i.name} (Bloqueado: ${i.blocked_stock} | Total: ${i.stock ?? 0})`
        }));
    }

    if (type === 'BLOCK' || type === 'OUT') {
      return items.map(i => ({
        ...i,
        label: `[${i.internal_code || 'S/C'}] ${i.name} (Disp: ${i.available_stock} | Bloqueado: ${i.blocked_stock} | Total: ${i.stock ?? 0})`
      }));
    }

    return items.map(i => ({
      ...i,
      label: `[${i.internal_code || 'S/C'}] ${i.name} (Stock: ${i.stock ?? 0} | Disp: ${i.available_stock})`
    }));
  });

  // Per-row FEFO suggestions and lot options
  rowFefoSuggestions = signal<Record<number, any>>({});
  rowLots = signal<Record<number, any[]>>({});

  movementTypes = [
    { value: 'IN', label: 'Entrada', description: 'Incrementa existencias al recibir nuevos productos o lotes.', icon: 'pi pi-arrow-down-left', color: '#22c55e', bg: 'bg-green-50' },
    { value: 'OUT', label: 'Salida', description: 'Descuenta stock por consumo, venta o despacho de mercaderia.', icon: 'pi pi-arrow-up-right', color: '#ef4444', bg: 'bg-red-50' },
    { value: 'ADJUSTMENT', label: 'Ajuste', description: 'Correccion de saldo para igualar la realidad fisica del deposito.', icon: 'pi pi-sync', color: '#3b82f6', bg: 'bg-blue-50' },
    { value: 'TRANSFER', label: 'Transferencia', description: 'Traslada existencias hacia otra sucursal del laboratorio.', icon: 'pi pi-arrow-right-arrow-left', color: '#8b5cf6', bg: 'bg-purple-50' },
    { value: 'BLOCK', label: 'Bloqueo / Cuarentena', description: 'Inmoviliza stock temporalmente por motivos de calidad o revisión.', icon: 'pi pi-lock', color: '#f59e0b', bg: 'bg-yellow-50' },
    { value: 'UNBLOCK', label: 'Desbloqueo', description: 'Libera stock previamente bloqueado o en cuarentena.', icon: 'pi pi-lock-open', color: '#10b981', bg: 'bg-teal-50' }
  ];

  get itemsFormArray() {
    return this.form.get('items') as FormArray;
  }

  constructor() {
    effect(() => {
      const type = this.form.get('type')?.value;
      untracked(() => {
        if (type) {
          this.selectedType.set(type);
          this.optionsCondensed.set(true);
        } else {
          this.selectedType.set('');
        }
      });
    });
  }

  async ngOnInit(): Promise<void> {
    const authBranches = (this.authService.currentUserValue as any)?.branches || [];
    this.userBranches.set(authBranches);
    try {
      const res = await firstValueFrom(this.branchService._getAll());
      const branches = res.data?.branches || [];
      this.localBranches.set(branches);
      if (!this.form.get('branchId')?.value && authBranches.length > 0) {
        this.form.patchValue({ branchId: authBranches[0].id });
      }
    } catch (e) {
      console.error('Error fetching branches', e);
    }

    this.form.get('branchId')?.valueChanges.subscribe(bid => {
      this.loadAvailableItems(bid);
    });

    this.route.paramMap.subscribe(async params => {
      const id = params.get('id');
      try {
        this.loading.set(true);
        if (id) {
          this.mode.set('edit');
          this.optionsCondensed.set(true);
          await Promise.all([
            this.loadAvailableItems(),
            this.loadMovement(id)
          ]);
        } else {
          this.mode.set('create');
          await this.loadAvailableItems();
          
          const queryParams = this.route.snapshot.queryParamMap;
          const prefillType = queryParams.get('type');
          const prefillItemId = queryParams.get('itemId');

          if (prefillType) {
            this.selectType(prefillType);
            if (prefillItemId) {
              this.addItemRow({ itemId: prefillItemId });
            }
          }
        }
      } catch (error) {
        console.error('Error initializing form', error);
      } finally {
        this.loading.set(false);
      }
    });
  }

  async loadAvailableItems(branchId?: string) {
    try {
      const targetBranchId = branchId || this.form.get('branchId')?.value || this.branchId() || undefined;
      const res = await this.inventoryService.getAll('items', { limit: 5000, branchId: targetBranchId }); // BUG-09: raised from 1000; virtualScroll handles DOM performance
      // Filter out items without warehouse stock tracking (TEST_ITEM, SERVICE)
      const stockableTypes = ['CONSUMABLE', 'REACTIVE', 'STANDARD', 'PRODUCT', 'SPARE_PART'];
      const filtered = (res.data || []).filter((i: any) => stockableTypes.includes(i.type));

      this.availableItems.set(filtered.map((i: any) => {
        const available = (i.stock || 0) - (i.reserved_stock || 0) - (i.blocked_stock || 0);
        return { 
          label: `[${i.internal_code || 'S/C'}] ${i.name} (Disp: ${available} | Total: ${i.stock ?? 0})`, 
          value: i.id,
          internal_code: i.internal_code,
          stock: i.stock || 0,
          reserved_stock: i.reserved_stock || 0,
          blocked_stock: i.blocked_stock || 0,
          available_stock: available,
          name: i.name,
          type: i.type
        };
      }));
    } catch (error) {
      console.error("Error loading items", error);
    }
  }

  async loadMovement(id: string) {
    try {
      const res = await this.inventoryService.getOne('stock-movements', id);
      
      this.form.patchValue({
        id: res.id,
        v_: res.v_,
        branchId: res.branchId,
        destinationBranchId: res.destinationBranchId,
        type: res.type,
        description: res.description
      });
      this.selectedType.set(res.type);

      this.itemsFormArray.clear();
      if (res.items && res.items.length > 0) {
        res.items.forEach((item: any) => {
          this.addItemRow(item);
        });
      } else {
        this.addItemRow();
      }
      this.optionsCondensed.set(true);
    } catch (error) {
      console.error("Error loading movement", error);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar el movimiento' });
      this.goBack();
    }
  }

  selectType(typeValue: string) {
    const previousType = this.form.get('type')?.value;
    this.form.get('type')?.setValue(typeValue);
    this.selectedType.set(typeValue);
    this.optionsCondensed.set(true);

    if (typeValue === 'TRANSFER') {
      this.form.get('destinationBranchId')?.setValidators([Validators.required]);
    } else {
      this.form.get('destinationBranchId')?.clearValidators();
    }
    this.form.get('destinationBranchId')?.updateValueAndValidity();

    if (this.itemsFormArray.length === 0) {
      this.addItemRow();
    } else if (previousType !== typeValue) {
      // Re-trigger lots / suggestions if type changed
      this.itemsFormArray.controls.forEach((ctrl, idx) => {
        const itemId = ctrl.get('itemId')?.value;
        const qty = ctrl.get('quantity')?.value || 1;
        if (itemId) {
          this.onItemChanged(idx, itemId, qty);
        }
      });
    }
  }

  toggleOptions() {
    this.optionsCondensed.set(!this.optionsCondensed());
  }

  getSelectedType() {
    const val = this.form.get('type')?.value;
    return this.movementTypes.find(t => t.value === val);
  }

  addItemRow(itemData?: any) {
    if (itemData?.itemId && this.itemsFormArray.length === 1 && !this.itemsFormArray.at(0).get('itemId')?.value) {
      const firstRow = this.itemsFormArray.at(0);
      firstRow.patchValue({
        quantity: itemData.quantity ?? 1,
        reason: itemData.reason || '',
        lotId: itemData.lotId || null,
        lot_number: itemData.lot_number || itemData.lot?.lot_number || '',
        expiration_date: itemData.expiration_date ? new Date(itemData.expiration_date) : (itemData.lot?.expiration_date ? new Date(itemData.lot.expiration_date) : null),
        reception_date: itemData.reception_date ? new Date(itemData.reception_date) : (itemData.lot?.reception_date ? new Date(itemData.lot.reception_date) : (this.form.get('type')?.value === 'IN' ? new Date() : null)),
        itemId: itemData.itemId
      });
      return;
    }

    const currentType = this.form.get('type')?.value;
    const row = this.fb.group({
      itemId: [itemData?.itemId || null, Validators.required],
      quantity: [itemData?.quantity ?? 1, [Validators.required, Validators.min(1)]],
      reason: [itemData?.reason || ''],
      lotId: [itemData?.lotId || null],
      lot_number: [itemData?.lot_number || itemData?.lot?.lot_number || ''],
      expiration_date: [itemData?.expiration_date ? new Date(itemData.expiration_date) : (itemData?.lot?.expiration_date ? new Date(itemData.lot.expiration_date) : null)],
      reception_date: [itemData?.reception_date ? new Date(itemData.reception_date) : (itemData?.lot?.reception_date ? new Date(itemData.lot.reception_date) : (currentType === 'IN' ? new Date() : null))]
    });
    const index = this.itemsFormArray.length;
    row.get('itemId')?.valueChanges.subscribe(async (itemId) => {
      if (itemId) {
        await this.onItemChanged(index, itemId, row.get('quantity')?.value || 1);
      } else {
        const sugg = { ...this.rowFefoSuggestions() };
        delete sugg[index];
        this.rowFefoSuggestions.set(sugg);
        const lots = { ...this.rowLots() };
        delete lots[index];
        this.rowLots.set(lots);
      }
    });
    row.get('quantity')?.valueChanges.subscribe(async (qty) => {
      const itemId = row.get('itemId')?.value;
      if (itemId && qty && (this.form.get('type')?.value === 'OUT' || this.form.get('type')?.value === 'BLOCK')) {
        await this.onItemChanged(index, itemId, qty);
      }
    });
    this.itemsFormArray.push(row);

    if (itemData?.itemId) {
      this.onItemChanged(index, itemData.itemId, itemData.quantity || 1);
    }
  }

  async onItemChanged(index: number, itemId: string, qty: number = 1) {
    try {
      const [lots, fefo] = await Promise.all([
        this.inventoryService.getItemLots(itemId).catch(() => []),
        this.inventoryService.getFefoSuggestion(itemId, qty).catch(() => null)
      ]);
      const currentLots = { ...this.rowLots() };
      currentLots[index] = (lots || []).filter((l: any) => l.status === 'ACTIVE' && l.current_quantity > 0).map((l: any) => ({
        label: `Lote ${l.lot_number} (Disp: ${l.current_quantity - (l.reserved_quantity || 0) - (l.blocked_quantity || 0)}${l.expiration_date ? ' | Vto: ' + new Date(l.expiration_date).toLocaleDateString() : ' | Sin Vto'})`,
        value: l.id,
        lot: l
      }));
      this.rowLots.set(currentLots);

      if (fefo) {
        const currentSugg = { ...this.rowFefoSuggestions() };
        currentSugg[index] = fefo;
        this.rowFefoSuggestions.set(currentSugg);
      }
    } catch (e) {
      console.error('Error fetching lot FEFO data for row', e);
    }
  }

  removeItemRow(index: number) {
    this.itemsFormArray.removeAt(index);
    const sugg = { ...this.rowFefoSuggestions() };
    delete sugg[index];
    this.rowFefoSuggestions.set(sugg);
    const lots = { ...this.rowLots() };
    delete lots[index];
    this.rowLots.set(lots);
    if (this.itemsFormArray.length === 0 && this.form.get('type')?.value) {
      this.addItemRow();
    }
  }

  async save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.messageService.add({
        severity: 'warn',
        summary: 'Campos incompletos',
        detail: 'Complete todos los campos requeridos.'
      });
      return;
    }

    const payload = this.form.getRawValue();
    // Validate quantities
    const items = payload.items;
    
    this.invalidItems.set(new Set());
    this.errorMessage.set(null);

    // PRE-VALIDATION
    if (payload.type === 'OUT' || payload.type === 'BLOCK' || payload.type === 'UNBLOCK') {
      const errors: string[] = [];
      const invalidIds = new Set<string>();
      
      for (const itemMov of items) {
        const itemDef = this.availableItems().find(x => x.value === itemMov.itemId);
        if (itemDef) {
            if (payload.type === 'OUT' && itemDef.available_stock < itemMov.quantity) {
                errors.push(`${itemDef.name} (Disponible: ${itemDef.available_stock}, Solicitado: ${itemMov.quantity})`);
                invalidIds.add(itemMov.itemId);
            } else if (payload.type === 'BLOCK' && itemDef.available_stock < itemMov.quantity) {
                errors.push(`${itemDef.name} (Disponible: ${itemDef.available_stock}, A Bloquear: ${itemMov.quantity})`);
                invalidIds.add(itemMov.itemId);
            } else if (payload.type === 'UNBLOCK' && itemDef.blocked_stock < itemMov.quantity) {
                errors.push(`${itemDef.name} (Bloqueado actual: ${itemDef.blocked_stock}, A Desbloquear: ${itemMov.quantity})`);
                invalidIds.add(itemMov.itemId);
            }
        }
      }
      if (errors.length > 0) {
          this.invalidItems.set(invalidIds);
          const errMsg = `Stock Insuficiente / Acción inválida. Revise: ${errors.join(' | ')}`;
          this.errorMessage.set(errMsg);
          this.messageService.add({ 
              severity: 'error', 
              summary: 'Error de Validación', 
              detail: errMsg,
              life: 8000
          });
          return;
      }
    }

    if (payload.type === 'TRANSFER') {
      if (!payload.destinationBranchId) {
        this.messageService.add({ severity: 'error', summary: 'Error de Validación', detail: 'Debe seleccionar una sucursal destino para la transferencia.' });
        return;
      }
      if (payload.branchId && payload.branchId === payload.destinationBranchId) {
        this.messageService.add({ severity: 'error', summary: 'Error de Validación', detail: 'La sucursal destino debe ser diferente a la sucursal de origen.' });
        return;
      }
    }

    try {
      this.isSaving.set(true);
      
      // Clean payload
      const dataToSave: any = {
        branchId: payload.branchId || undefined,
        destinationBranchId: payload.type === 'TRANSFER' ? payload.destinationBranchId : undefined,
        type: payload.type,
        description: payload.description,
        items: payload.items.map((i: any) => ({
          itemId: i.itemId,
          quantity: Number(i.quantity),
          reason: i.reason,
          lotId: i.lotId || undefined,
          lot_number: i.lot_number ? i.lot_number.trim() : undefined,
          expiration_date: i.expiration_date ? (i.expiration_date instanceof Date ? i.expiration_date.toISOString() : new Date(i.expiration_date).toISOString()) : undefined,
          reception_date: i.reception_date ? (i.reception_date instanceof Date ? i.reception_date.toISOString() : new Date(i.reception_date).toISOString()) : undefined
        }))
      };

      if (this.mode() === 'edit' && payload.id) {
        dataToSave.v_ = payload.v_;
        await this.inventoryService.update('stock-movements', payload.id, dataToSave);
        this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Movimiento actualizado correctamente' });
      } else {
        await this.inventoryService.create('stock-movements', dataToSave);
        this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Movimiento registrado correctamente' });
      }
      
      this.goBack();
    } catch (error) {
      console.error(error);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar el movimiento' });
    } finally {
      this.isSaving.set(false);
    }
  }

  goBack() {
    this.location.back();
  }
}
